import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings2, Save, CalendarClock, PlayCircle } from "lucide-react";

type SettingsRow = {
  max_parallel_articles: number;
  requests_per_minute: number;
  max_attempts: number;
  inter_article_delay_ms: number;
  max_body_bytes: number;
  schedule_enabled: boolean;
  schedule_cron: string;
  schedule_scope: "missing" | "all";
  schedule_mode: "run" | "dry_run";
  schedule_last_run_at?: string | null;
  schedule_next_run_at?: string | null;
  single_active_per_scope: boolean;
  per_scope_concurrency: Record<string, number>;
  per_scope_retry_backoff: Record<string, { max_attempts?: number; strategy?: "exponential" | "linear" | "fixed"; base_ms?: number; max_ms?: number }>;
  failure_notify_webhook_url: string | null;
  failure_notify_email: string | null;
  updated_at?: string;
};

const DEFAULTS: SettingsRow = {
  max_parallel_articles: 3,
  requests_per_minute: 60,
  max_attempts: 4,
  inter_article_delay_ms: 250,
  max_body_bytes: 200_000,
  schedule_enabled: false,
  schedule_cron: "0 3 * * *",
  schedule_scope: "missing",
  schedule_mode: "run",
  single_active_per_scope: false,
  per_scope_concurrency: {},
  per_scope_retry_backoff: {},
  failure_notify_webhook_url: null,
  failure_notify_email: null,
};

const KNOWN_SCOPES = ["all", "missing", "single", "retry", "retry-bulk", "scheduled", "test", "custom"];


const NUM_FIELDS: Array<{
  key: "max_parallel_articles" | "requests_per_minute" | "max_attempts" | "inter_article_delay_ms" | "max_body_bytes";
  label: string; hint: string; min: number; max: number;
}> = [
  { key: "max_parallel_articles", label: "Max parallel articles", hint: "Worker concurrency (1–16)", min: 1, max: 16 },
  { key: "requests_per_minute", label: "Embedding requests/min", hint: "Gateway rate limit (1–600)", min: 1, max: 600 },
  { key: "max_attempts", label: "Max retry attempts", hint: "Per embedding call (1–8)", min: 1, max: 8 },
  { key: "inter_article_delay_ms", label: "Inter-article delay (ms)", hint: "Pause between articles", min: 0, max: 10000 },
  { key: "max_body_bytes", label: "Max article body bytes", hint: "Validation cap", min: 1000, max: 2_000_000 },
];

const CRON_PRESETS: Array<{ label: string; value: string }> = [
  { label: "Daily 03:00 UTC", value: "0 3 * * *" },
  { label: "Twice daily (03,15)", value: "0 3,15 * * *" },
  { label: "Weekly Mon 03:00", value: "0 3 * * 1" },
  { label: "Hourly", value: "0 * * * *" },
];

function isValidCron(expr: string): boolean {
  const parts = expr.trim().split(/\s+/);
  return parts.length === 5 && parts.every((p) => /^[\d*/,\-]+$/.test(p));
}

export function KbSettingsPanel() {
  const [values, setValues] = useState<SettingsRow>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("kb_ingest_settings").select("*").maybeSingle();
      if (data) setValues({ ...DEFAULTS, ...(data as SettingsRow) });
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    if (!isValidCron(values.schedule_cron)) {
      toast.error("Invalid cron expression (need 5 space-separated fields)");
      return;
    }
    setSaving(true);
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const { error } = await supabase
      .from("kb_ingest_settings")
      .upsert({ id: true, ...values, updated_by: userId }, { onConflict: "id" });
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Settings saved");
  };

  const testSchedule = async () => {
    if (!isValidCron(values.schedule_cron)) {
      toast.error("Fix the cron expression before testing.");
      return;
    }
    const t = toast.loading("Dispatching test dry-run…");
    const { data, error } = await supabase.functions.invoke("kb-ingest-scheduler", {
      body: { test: true },
    });
    toast.dismiss(t);
    if (error) { toast.error(`Test failed: ${error.message}`); return; }
    const res = data as { ok?: boolean; blocked?: boolean; reason?: string; dispatched?: number; job_id?: string | null; skipped?: string };
    if (res?.blocked) toast.warning(`Blocked: ${res.reason ?? "active job exists"}`);
    else if (res?.skipped) toast.info(`Skipped: ${res.skipped}`);
    else if (res?.job_id) toast.success(`Test dry-run dispatched (${res.dispatched} articles)`);
    else toast.message("Scheduler test complete", { description: JSON.stringify(res).slice(0, 240) });
  };

  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <Settings2 className="size-5 text-primary" />
        <div>
          <h2 className="font-display text-base font-bold">KB ingest settings</h2>
          <p className="font-mono text-xs text-muted-foreground">Concurrency, rate limits, and schedule.</p>
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {NUM_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label htmlFor={`set-${f.key}`} className="text-xs">{f.label}</Label>
                <Input
                  id={`set-${f.key}`}
                  type="number" min={f.min} max={f.max}
                  value={values[f.key]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [f.key]: Math.max(f.min, Math.min(f.max, Number(e.target.value || 0))) }))
                  }
                />
                <p className="font-mono text-[10px] text-muted-foreground">{f.hint}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-lg border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <CalendarClock className="size-4 text-primary" />
                <div>
                  <p className="text-sm font-semibold">Scheduled reindex</p>
                  <p className="font-mono text-[10px] text-muted-foreground">
                    Cron tick every 15 min checks this schedule.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="sched-enabled" className="text-xs">Enabled</Label>
                <Switch
                  id="sched-enabled"
                  checked={values.schedule_enabled}
                  onCheckedChange={(c) => setValues((v) => ({ ...v, schedule_enabled: c }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="sched-cron" className="text-xs">Cron expression (UTC)</Label>
                <Input
                  id="sched-cron"
                  value={values.schedule_cron}
                  onChange={(e) => setValues((v) => ({ ...v, schedule_cron: e.target.value }))}
                  placeholder="0 3 * * *"
                />
                <div className="flex flex-wrap gap-1 pt-1">
                  {CRON_PRESETS.map((p) => (
                    <button
                      type="button" key={p.value}
                      onClick={() => setValues((v) => ({ ...v, schedule_cron: p.value }))}
                      className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] hover:bg-muted"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sched-scope" className="text-xs">Scope</Label>
                <select
                  id="sched-scope"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={values.schedule_scope}
                  onChange={(e) => setValues((v) => ({ ...v, schedule_scope: e.target.value as "missing" | "all" }))}
                >
                  <option value="missing">Missing only</option>
                  <option value="all">All articles</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sched-mode" className="text-xs">Mode</Label>
                <select
                  id="sched-mode"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={values.schedule_mode}
                  onChange={(e) => setValues((v) => ({ ...v, schedule_mode: e.target.value as "run" | "dry_run" }))}
                >
                  <option value="run">Reindex (write)</option>
                  <option value="dry_run">Dry run</option>
                </select>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="single-active" className="text-xs">Allow only one active job per scope</Label>
                <Switch
                  id="single-active"
                  checked={values.single_active_per_scope}
                  onCheckedChange={(c) => setValues((v) => ({ ...v, single_active_per_scope: c }))}
                />
              </div>
              <Button size="sm" variant="outline" onClick={testSchedule}>
                <PlayCircle className="size-4" /> Test schedule
              </Button>
            </div>

            {(values.schedule_last_run_at || values.schedule_next_run_at) && (
              <p className="mt-3 font-mono text-[11px] text-muted-foreground">
                {values.schedule_last_run_at && <>last: {new Date(values.schedule_last_run_at).toLocaleString()}</>}
                {values.schedule_last_run_at && values.schedule_next_run_at && " · "}
                {values.schedule_next_run_at && <>next: {new Date(values.schedule_next_run_at).toLocaleString()}</>}
              </p>
            )}
          </div>

          {/* Per-scope concurrency caps */}
          <div className="mt-5 rounded-lg border border-border bg-background/40 p-3">
            <p className="text-sm font-semibold mb-1">Per-scope concurrency caps</p>
            <p className="font-mono text-[10px] text-muted-foreground mb-3">
              Max number of <em>jobs</em> allowed to run in parallel per scope. 0 = unlimited. Overrides the single-active toggle when set.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {KNOWN_SCOPES.map((sc) => (
                <div key={sc} className="space-y-1">
                  <Label htmlFor={`cap-${sc}`} className="text-[11px] font-mono">{sc}</Label>
                  <Input
                    id={`cap-${sc}`}
                    type="number" min={0} max={20}
                    value={values.per_scope_concurrency[sc] ?? 0}
                    onChange={(e) => {
                      const n = Math.max(0, Math.min(20, Number(e.target.value || 0)));
                      setValues((v) => {
                        const next = { ...v.per_scope_concurrency };
                        if (n === 0) delete next[sc]; else next[sc] = n;
                        return { ...v, per_scope_concurrency: next };
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Per-scope retry backoff */}
          <div className="mt-5 rounded-lg border border-border bg-background/40 p-3">
            <p className="text-sm font-semibold mb-1">Per-scope retry backoff</p>
            <p className="font-mono text-[10px] text-muted-foreground mb-3">
              Override embedding retry policy per scope. Blank fields fall back to the global Max retry attempts.
              Strategy: <code>exponential</code> = base·2^n, <code>linear</code> = base·n, <code>fixed</code> = base. Capped by max_ms.
            </p>
            <div className="space-y-2">
              {KNOWN_SCOPES.map((sc) => {
                const cfg = values.per_scope_retry_backoff[sc] ?? {};
                const update = (patch: Partial<typeof cfg>) => {
                  setValues((v) => {
                    const next = { ...v.per_scope_retry_backoff };
                    const merged = { ...(next[sc] ?? {}), ...patch };
                    // Clean: drop entry if everything blank/default
                    const isEmpty =
                      (merged.max_attempts == null || Number.isNaN(merged.max_attempts)) &&
                      !merged.strategy &&
                      (merged.base_ms == null || Number.isNaN(merged.base_ms)) &&
                      (merged.max_ms == null || Number.isNaN(merged.max_ms));
                    if (isEmpty) delete next[sc]; else next[sc] = merged;
                    return { ...v, per_scope_retry_backoff: next };
                  });
                };
                return (
                  <div key={sc} className="grid grid-cols-5 items-center gap-2">
                    <Label className="text-[11px] font-mono">{sc}</Label>
                    <Input
                      type="number" min={1} max={10} placeholder="max"
                      value={cfg.max_attempts ?? ""}
                      onChange={(e) => update({ max_attempts: e.target.value === "" ? undefined : Math.max(1, Math.min(10, Number(e.target.value))) })}
                    />
                    <select
                      className="h-10 rounded-md border border-input bg-background px-2 text-xs"
                      value={cfg.strategy ?? ""}
                      onChange={(e) => update({ strategy: (e.target.value || undefined) as typeof cfg.strategy })}
                    >
                      <option value="">strategy</option>
                      <option value="exponential">exponential</option>
                      <option value="linear">linear</option>
                      <option value="fixed">fixed</option>
                    </select>
                    <Input
                      type="number" min={50} max={60000} placeholder="base ms"
                      value={cfg.base_ms ?? ""}
                      onChange={(e) => update({ base_ms: e.target.value === "" ? undefined : Math.max(50, Math.min(60000, Number(e.target.value))) })}
                    />
                    <Input
                      type="number" min={100} max={300000} placeholder="max ms"
                      value={cfg.max_ms ?? ""}
                      onChange={(e) => update({ max_ms: e.target.value === "" ? undefined : Math.max(100, Math.min(300000, Number(e.target.value))) })}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Failure notifications */}
          <div className="mt-5 rounded-lg border border-border bg-background/40 p-3">
            <p className="text-sm font-semibold mb-1">Failure notifications</p>
            <p className="font-mono text-[10px] text-muted-foreground mb-3">
              Sent when a kb-ingest job ends with status <code>failed</code> or <code>partial</code>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="notify-webhook" className="text-xs">Webhook URL</Label>
                <Input
                  id="notify-webhook"
                  type="url" placeholder="https://hooks.example.com/kb-ingest"
                  value={values.failure_notify_webhook_url ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, failure_notify_webhook_url: e.target.value || null }))}
                />
                <p className="font-mono text-[10px] text-muted-foreground">POST JSON: job_id, status, scope, mode, failed, processed, total, error.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="notify-email" className="text-xs">Email (Slack/email gateway)</Label>
                <Input
                  id="notify-email"
                  type="email" placeholder="alerts@company.com"
                  value={values.failure_notify_email ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, failure_notify_email: e.target.value || null }))}
                />
                <p className="font-mono text-[10px] text-muted-foreground">Forwarded via webhook payload field `notify_email` for downstream relays.</p>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Button size="sm" onClick={save} disabled={saving}>
              <Save className="size-4" /> Save settings
            </Button>
          </div>

        </>
      )}
    </section>
  );
}
